import { defineStore } from "pinia";
import {
  checkEmail,
  fetchCurrentUser,
  loginWithEmail,
  loginWithToken,
  selectOrganization,
  sendMagicLinkEmail,
} from "../api";
import router from "@/router";
import { ROUTE_NAME } from "@/shared/constants/routes";
import { getNameInitials } from "@/shared/utils/helpers";
import { useAdminAvatar } from "@/modules/profile/stores/adminAvatar";
import {
  EVENT_NAMES,
  addMixpanelEvent,
  mixpanelLogin,
  mixpanelLogout,
} from "@/shared/utils/analytics";
import * as Sentry from "@sentry/vue";
import type { AccessToken, AdminOrganization } from "@/api/model";
import type { AdminEmailCheckResponse } from "@/api/model";
import type User from "@/shared/datamodels/user";
import type { ApiError } from "@/types";

export interface AuthState {
  user: User | null;
  token: AccessToken | null;
  singleOrgUser: boolean | null;
}

export interface LoginResult {
  success: boolean;
  user?: User;
  error?: string;
}

export interface AuthGetters {
  isLoggedIn: (state: AuthState) => boolean;
  isLoggedInButNotAdmin: (state: AuthState) => boolean;
  userInitials: (state: AuthState) => string;
  isTfaEmployee: (state: AuthState) => boolean;
  canManagePayments: (state: AuthState) => boolean;
  servicesInstructorId: (state: AuthState) => string | null;
  defaultOrg: (state: AuthState) => AdminOrganization | undefined;
  isSingleOrgUser: (state: AuthState) => boolean | null;
  hasMultipleAdminOrgs: (state: AuthState) => boolean;
  loggedInUserId: (state: AuthState) => number | undefined;
  [key: string]: any;
}

export interface AuthActions {
  login(options: LoginOptions): Promise<LoginResult>;
  logout(): Promise<void>;
  processSuccessfulLogin(loginResult: AccessToken): Promise<void>;
  selectOrganization(organizationId: number): Promise<void>;
  updateDefaultOrganization(organization: Partial<AdminOrganization>): void;
  checkEmail(email: string): Promise<AdminEmailCheckResponse | ApiError>;
  sendMagicLinkEmail(email: string): Promise<void | ApiError>;
}

export type AuthStore = AuthState & AuthGetters & AuthActions;

interface LoginOptions {
  email?: string;
  password?: string;
  magicLinkToken?: string;
}

/*
 * This store is used to manage the user's authentication state.
 */

export const useAuthStore = defineStore<
  "authStore",
  AuthState,
  AuthGetters,
  AuthActions
>("authStore", {
  persist: {
    afterRestore: async (ctx) => {
      // The persisted user is a snapshot: the payments tier and the user's own
      // instructor record are server-owned and change while a session sits in
      // localStorage (MAI-4956 / MAI-5125), so refresh on every app load. The
      // stale copy stays in place until the fetch lands, so nothing flickers.
      const user = ctx.store.user;
      if (user && ctx.store.token?.access) {
        try {
          ctx.store.user = await fetchCurrentUser();
          ctx.store.singleOrgUser = !ctx.store.user?.is_tfa_employee;
        } catch (error) {
          console.error("Error refreshing current user on restore:", error);
        }
      }
    },
  },
  state: (): AuthState => ({
    user: null,
    token: null,
    singleOrgUser: null,
  }),
  getters: {
    isLoggedIn: (state: AuthState): boolean =>
      Boolean(state.user?.is_admin && state.token),
    isLoggedInButNotAdmin: (state: AuthState): boolean =>
      Boolean(!state.user?.is_admin && state.token),
    userInitials: (state: AuthState): string =>
      getNameInitials(state.user?.name ?? ""),
    isTfaEmployee: (state: AuthState): boolean =>
      Boolean(state.user?.is_tfa_employee),
    // TFA employees always have the payments tier (MAI-4956).
    canManagePayments: (state: AuthState): boolean =>
      Boolean(state.user?.is_tfa_employee || state.user?.can_manage_payments),
    servicesInstructorId: (state: AuthState): string | null =>
      state.user?.services_instructor_id ?? null,
    defaultOrg: (state: AuthState): AdminOrganization | undefined =>
      state.user?.default_organization,
    isSingleOrgUser: (state: AuthState): boolean | null => state.singleOrgUser,
    hasMultipleAdminOrgs: (state: AuthState): boolean =>
      (state.user?.admin_organization_ids?.length ?? 0) > 1,
    loggedInUserId: (state: AuthState): number | undefined => state.user?.id,
  },
  actions: {
    async login(options: LoginOptions): Promise<LoginResult> {
      const { magicLinkToken, email, password } = options;

      try {
        if (magicLinkToken) {
          const loginResult = await loginWithToken({ token: magicLinkToken });
          if ("error" in loginResult) {
            return handleLoginError(loginResult.error);
          }

          addMixpanelEvent(EVENT_NAMES.USER.AdminLoggedIn, {
            method: "Magic Link",
          });

          await this.processSuccessfulLogin(loginResult);
          return { success: true, user: this.user as User };
        }

        if (email && password) {
          const loginResult = await loginWithEmail(email, password);
          if (!loginResult) {
            return { success: false, error: "Login failed" };
          }
          if ("error" in loginResult) {
            return handleLoginError(loginResult.error);
          }

          addMixpanelEvent(EVENT_NAMES.USER.AdminLoggedIn, {
            method: "Magic Link",
          });
          await this.processSuccessfulLogin(loginResult);
          return { success: true, user: this.user as User };
        }

        return { success: false, error: "No valid login credentials provided" };
      } catch (error) {
        console.error("Error during login:", error);
        return {
          success: false,
          error: "An error occurred during login",
        };
      }
    },

    async logout(): Promise<void> {
      clearLocalStorage();
      this.$reset();
      mixpanelLogout();
      Sentry.setUser(null);
      await router.push({ name: ROUTE_NAME.LOGIN });
    },

    async processSuccessfulLogin(loginResult: AccessToken): Promise<void> {
      this.token = loginResult;
      this.user = await fetchCurrentUser();
      this.singleOrgUser = !this.user?.is_tfa_employee;

      await updateAdminProfileImage();
      if (this.user) {
        mixpanelLogin(this.user);
        setSentryUserContext(this.user);
      }
    },

    async selectOrganization(organizationId: number): Promise<void> {
      // The new tokens carry that org's `organization_id` and `admin` role claims,
      // so re-running the post-login work is all it takes to switch profiles.
      await this.processSuccessfulLogin(
        await selectOrganization(organizationId)
      );
    },

    updateDefaultOrganization(organization: Partial<AdminOrganization>): void {
      if (!this.user?.default_organization) return;

      this.user.default_organization = {
        ...this.user.default_organization,
        ...organization,
      };

      setSentryUserContext(this.user);
    },

    async checkEmail(
      email: string
    ): Promise<AdminEmailCheckResponse | ApiError> {
      return await checkEmail(email);
    },

    async sendMagicLinkEmail(email: string): Promise<void | ApiError> {
      return await sendMagicLinkEmail(email);
    },
  },
});

function handleLoginError(error: string): LoginResult {
  if (error === "Password change required") {
    return { success: false, error: "Password change required" };
  }
  return { success: false, error: "Login error" };
}

async function updateAdminProfileImage() {
  const { updateLatestAdminProfileImage } = useAdminAvatar();
  await updateLatestAdminProfileImage();
}

function setSentryUserContext(user: User): void {
  Sentry.setUser({
    id: String(user.id),
    email: user.email,
    username: user.name,
    organization_id: user.default_organization?.id,
  });
}

function clearLocalStorage() {
  localStorage.removeItem("authStore");
  localStorage.removeItem("adminAvatar");
  localStorage.removeItem("signedCookies");
}

export type AuthStoreType = ReturnType<typeof useAuthStore>;
